import { FunctionComponent, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { faChevronDown, faChevronUp, faClock } from '@fortawesome/pro-regular-svg-icons';
import useNonInitialEffect from '@versiondos/hooks';
import styles from './TypeSelector.module.scss';
import ClickAwayListener from 'components/CreateBooking/UI/ClickAwayListener';
import { Option } from '@spike/model';

export interface TypeSelectorProps {
    id?: string;
    selectedOption?: Option<string>;
    placeholder?: string;
    options: Array<Option<string>>;
    disabled?: boolean;
    className?: string;
    required?: boolean;
    onSelect: (option: Option<string>) => void;
}

export const TypeSelector: FunctionComponent<TypeSelectorProps> = props => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [defaultState, setDefault] = useState<string | undefined>(undefined);

    const changeHandler = (idSelected: string) => {
        const optionSelected = props.options.find(option => option.id === idSelected)!;
        props.onSelect(optionSelected);
        setDefault(optionSelected.name);
    };

    useNonInitialEffect(() => {
        const timer: NodeJS.Timer = setTimeout(() => {
            if (defaultState) {
                setDefault(undefined);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [defaultState]);

    const header = (
        <div className={styles.headerContainer}>
            <div className={styles.textContainer}>
                <span
                    className={clsx(styles.header, {
                        [styles.placeholder]: !props.selectedOption
                    })}
                >
                    {props.selectedOption?.name ? props.selectedOption?.name : props.placeholder}
                </span>
            </div>
            <div className={styles.chevronContainer}>
                <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    color={isCollapsed ? '#7A7A7A' : '#222222'}
                    className={styles.chevron}
                />
            </div>
        </div>
    );

    const list = (
        <div className={styles.listContainer}>
            {props.options.map((opt, index) => (
                <div
                    className={styles.itemContainer}
                    key={opt.id}
                    id={`type_option_${index}`}
                    onClick={() => changeHandler(opt.id)}
                >
                    <div className={styles.itemContent}>
                        <div className={clsx(styles.itemContainer, styles.typeContainer)}>
                            <span className={clsx(styles.item, styles.sectionType)}>{opt.name}</span>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const collapsed = (
        <div
            className={clsx(styles.border, styles.collapsedContainer, [styles.optionNotSelected])}
            onClick={() => setIsCollapsed(false)}
        >
            {header}
        </div>
    );

    const expanded = (
        <div
            className={clsx(styles.border, styles.expandedContainer)}
            onClick={() => setIsCollapsed(true)}
        >
            {header}
            {list}
        </div>
    );

    return (
        <ClickAwayListener onClickAway={() => setIsCollapsed(true)}>
            <div className={clsx(styles.container, props.className)}>
                <div
                    className={styles.dropdownContainer}
                    id="type_list"
                >
                    {isCollapsed ? collapsed : expanded}
                </div>
                {props.selectedOption && <div className={styles.customData}></div>}
            </div>
        </ClickAwayListener>
    );
};

export default TypeSelector;
